import React, { Component, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { rgba } from 'polished';
import styled from 'styled-components/macro';
import detectIt from 'detect-it';
import { ThemeProps } from 'types/styled';

const { hasTouch, hasMouse } = detectIt;
const isTouch = hasTouch && !hasMouse;

const body = document.body;
const modalRoot = document.getElementById('modal');

const ModalDiv = styled('div')<ThemeProps<'div'>>`
  background-color: ${({ theme, backgroundOpacity, openFromSide }): string =>
    openFromSide !== undefined
      ? 'transparent'
      : rgba(theme.NightBlue, backgroundOpacity || 0.85)};
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  overflow: auto;
  overflow-y: ${({ isTouch }): string => (isTouch ? 'scroll' : 'auto')};
  overscroll-behavior-y: contain;
  display: ${({ isFullScreen }): string => (isFullScreen ? 'block' : 'flex')};
  justify-content: ${({ openFromSide }): string =>
    openFromSide !== undefined ? openFromSide : 'center'};
  -webkit-overflow-scrolling: touch;
`;

export const ModalContent = styled('div')<any>`
  margin: ${({ theme, margin }): string =>
    margin ? margin : theme.spacing.small};
  align-self: ${({ alignSelf }): string => (alignSelf ? alignSelf : 'center')};
  width: 100%;
  max-width: ${({ isNarrow, maxWidth }): string =>
    maxWidth ? maxWidth : isNarrow ? '35.625em' : '44em'};
  background-color: ${({ contentBackground }): string =>
    contentBackground || 'white'};
  color: ${({ theme }): string => theme.NightBlue};
  ${({ isBoxShadowed }): string =>
    isBoxShadowed && 'box-shadow: 0 0 4.5em rgba(0, 0, 0, 0.2)'};

  ${({ borderRadius }): string =>
    borderRadius && `border-radius: ${borderRadius}`};

  @media screen and (min-width: 30em) {
    margin: ${({ theme, margin }): string =>
      margin ? margin : `${theme.spacing.large} auto`};
  }

  @media screen and (min-width: 44em) {
    margin: ${({ theme, margin }): string =>
      margin ? margin : `${theme.spacing.huge} auto`};
  }
`;

const ESCAPE_KEY = 27;

interface IModalProps {
  hide?: () => void;
  /** The children nodes that will be wrapped by this component */
  children: ReactNode;
  /** Should we close the modal when clicking outside it? */
  canClickOutside?: boolean;
  /** Optional className, required for styled-components composition */
  className?: string;
  isNarrow?: boolean;
  maxWidth?: string;
  contentBackground?: string;
  backgroundOpacity?: number;
  isBoxShadowed?: boolean;
  borderRadius?: string;
  isFullScreen?: boolean;
  margin?: string;
  alignSelf?: string;
  openFromSide?: string;
}

/**
 * Due to adding stopPropagation for iOs on line 136
 * in subscribe modal a dropdown can not be closed when another opens
 * That is why the function for checking only such dropdowns are added
 */
const getDropdownParent = (target: HTMLElement): boolean => {
  const parentsArray = [...document.querySelectorAll('#subscribe-dropdown')];
  return parentsArray.some((parent: Element): boolean =>
    parent.contains(target)
  );
};

/**
 * A Modal component that is an abstraction around the React Portal API. You can pass Modal
 * any Child content you want and it will open in a modal window.
 */
class Modal extends Component<IModalProps> {
  el: HTMLElement;
  modalElement: HTMLElement | null = null;
  root: HTMLElement | null = null;

  static defaultProps = { canClickOutside: true };

  constructor(props: IModalProps) {
    super(props);
    // Create a div that we'll render the modal into. Because each
    // Modal component has its own element, we can render multiple
    // modal components into the modal container.
    this.el = document.createElement('div');
    this.root = document.getElementById('root');
  }

  handleKeyDown = ({ keyCode }: { keyCode: number }): void => {
    const { hide } = this.props;
    if (keyCode === ESCAPE_KEY && hide) {
      hide();
    }
  };

  handleClick = ({ target }: { target: any }): void => {
    if (
      this.modalElement === target &&
      this.props.canClickOutside &&
      this.props.hide
    ) {
      this.props.hide();
    }
  };

  componentDidMount(): void {
    // Append the element into the DOM on mount. We'll render
    // into the modal container element (see the HTML tab).
    modalRoot?.appendChild(this.el); // eslint-disable-line no-unused-expressions
    body.classList.add('modal-open');
    if (this.props.hide) {
      this.root?.addEventListener('keydown', this.handleKeyDown);
    }
  }

  componentWillUnmount(): void {
    // Remove the element from the DOM when we unmount
    modalRoot?.removeChild(this.el); // eslint-disable-line no-unused-expressions
    body.classList.remove('modal-open');

    if (this.props.hide) {
      this.root?.removeEventListener('keydown', this.handleKeyDown);
    }
  }

  render(): any {
    const {
      className,
      children,
      contentBackground,
      backgroundOpacity,
      isBoxShadowed = true,
      borderRadius,
      isFullScreen,
      margin,
      alignSelf,
      openFromSide,
    } = this.props;

    let marginContent = margin;
    if (openFromSide !== undefined) {
      marginContent = '0px';
    }
    // Use a portal to render the children into the element
    return ReactDOM.createPortal(
      // Any valid React child: JSX, strings, arrays, etc.
      <ModalDiv
        ref={(el: HTMLElement): void => {
          this.modalElement = el;
        }}
        onClick={this.handleClick}
        className={className}
        isTouch={isTouch}
        backgroundOpacity={backgroundOpacity}
        isFullScreen={isFullScreen}
        openFromSide={openFromSide}
      >
        <ModalContent
          isNarrow={this.props.isNarrow}
          maxWidth={this.props.maxWidth}
          contentBackground={contentBackground}
          isBoxShadowed={isBoxShadowed}
          borderRadius={borderRadius}
          margin={marginContent}
          alignSelf={alignSelf}
          onClick={
            /** 
            To prevent iOS from going into a dimmed state due to the onClick handler of the ModalDiv
            We want to prevent the onClick event bubbeling from the Content element.
           */
            (event: any): void => {
              const parent = getDropdownParent(event.target);
              if (!parent) event.stopPropagation();
            }
          }
        >
          {children}
        </ModalContent>
      </ModalDiv>,
      // A DOM element
      this.el
    );
  }
}

export default Modal;
